/** Open a frame or task in its own project: switch first, then wait for its
 *  frames (on the wall the frame is fetched when no loaded page has it). */
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { waitForFrame } from "@/components/panels/notifications/wait-for-frame";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { enterExplicitFocus } from "@/lib/focus-presentation";
import { reportMissingFrame } from "@/lib/open-frames";
import { selectProjectByIdentity } from "@/lib/select-project-space";
import { useProjectStore } from "@/stores/project-store";
import { isWallActive } from "@/stores/workspace-view-store";
import {
  captureTaskOpenIntent,
  openTaskDetails,
} from "@/lib/right-panel-navigation";

export interface ProjectItemTarget {
  id: string;
  projectId: string | null;
  projectName: string | null;
}

/** Switch to the item's project unless it is the current one. */
export async function enterProject(
  target: ProjectItemTarget,
  shouldContinue?: () => boolean,
): Promise<"same" | "switched" | "failed"> {
  if (shouldContinue && !shouldContinue()) return "failed";
  if (
    !target.projectId ||
    useProjectStore.getState().currentProject?.id === target.projectId
  )
    return "same";
  const switched = await selectProjectByIdentity(
    {
      projectId: target.projectId,
      projectName: target.projectName ?? undefined,
    },
    shouldContinue ? { shouldContinue } : undefined,
  );
  return switched ? "switched" : "failed";
}

/**
 * `focus`: the frame opens in focus mode (a desktop pin's Open) instead of
 * where the current context opens it; it waits for the frame's load.
 * `displayId`: the display that focus opens on (a pin's own).
 */
export async function openFrameInProject(
  frame: ProjectItemTarget,
  options: { waitForLoad?: boolean; focus?: boolean; displayId?: number } = {},
): Promise<void> {
  const isCurrent = captureDesktopChatAuthScopeGuard();
  const entered = await enterProject(frame, isCurrent);
  if (entered === "failed" || !isCurrent()) return;
  // The wall holds only the pages it loaded, so it may fetch the frame.
  if (
    (entered === "switched" ||
      options.waitForLoad ||
      options.focus ||
      isWallActive()) &&
    (await waitForFrame(frame.id)) === "timeout"
  ) {
    if (isCurrent()) reportMissingFrame(frame.id);
    return;
  }
  if (!isCurrent()) return;
  if (options.focus)
    enterExplicitFocus([frame.id], { displayId: options.displayId });
  else if (!activateFrameInCurrentContext(frame.id))
    reportMissingFrame(frame.id);
}

/** Preserve this opening's intent across project lookup and workspace reveal. */
export async function openTaskInProject(
  task: ProjectItemTarget,
): Promise<void> {
  const shouldContinue = captureTaskOpenIntent();
  const entered = await enterProject(task, shouldContinue);
  if (entered === "failed" || !shouldContinue()) return;
  await openTaskDetails(task.id, { shouldContinue });
}
